import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Low-contrast inline chip. No variant is a saturated filled pill any more.
 *
 * Prefer `StatusDot` + plain text wherever the badge encodes a lifecycle state —
 * `Badge` is for counts, labels and non-stateful metadata.
 */
const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 rounded-sm px-2.5 py-0.5",
    "text-xs font-medium leading-none whitespace-nowrap",
    "transition-[background-color,color] duration-100 ease-out-fast",
    "focus-visible:outline-none focus-fill",
    "[&_svg]:size-3 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "bg-hover text-text-secondary",
        secondary: "bg-hover text-text-tertiary",
        destructive: "bg-danger-subtle text-danger",
        // The `outline` name is kept for call sites; the edge is not. It now
        // reads as the recessed sibling of `default`, one step below the
        // `bg-hover` wash rather than one step outlined from it.
        outline: "bg-sunken text-text-secondary",
        accent: "bg-accent-subtle text-accent-text",
        success: "bg-success-subtle text-success",
        warning: "bg-warning-subtle text-warning",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
